import { z } from 'zod';

import { BooleanQueryParamSchema, IsoDateTimeSchema, UuidSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { DestinationHaulSchema, TripTypeRefSchema } from './trip-types.js';

/**
 * Region + Destination masters (ADR-0041).
 *
 * `Region` is a thin curated lookup synced from dr5hn's `all_regions`
 * (Asia, Europe, ...). It's mostly operator-read-only; the admin UI
 * lets them toggle `isActive` and re-order, that's it.
 *
 * `Destination` is the operator-managed master that powers the new lead
 * form picker. Each row is "Bali Beach Holiday" + region + country (+
 * optional state + optional city). The lead-form picker filters out
 * rows whose underlying country is inactive (server-side filter).
 */

// ----- Region -------------------------------------------------------------

export const RegionListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  name: z.string(),
  wikiDataId: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  /// Convenience denorm: how many active countries map to this region.
  /// Drives the "X countries" subtitle in the master list.
  countryCount: z.number().int(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type RegionListItem = z.infer<typeof RegionListItemSchema>;

export const RegionListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
});
export type RegionListQuery = z.infer<typeof RegionListQuerySchema>;

export const RegionListResponseSchema = paginatedResponseSchema(RegionListItemSchema);
export type RegionListResponse = z.infer<typeof RegionListResponseSchema>;

export const UpdateRegionRequestSchema = z.object({
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});
export type UpdateRegionRequest = z.infer<typeof UpdateRegionRequestSchema>;

// ----- Destination --------------------------------------------------------

const DestinationGeoRefSchema = z.object({
  id: UuidSchema,
  name: z.string(),
});

export const DestinationListItemSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  /// Region OR country is required (at least one). Both are returned
  /// nullable here; consumers should fall back to country when region
  /// is null (and vice versa) for display.
  region: DestinationGeoRefSchema.nullable(),
  country: DestinationGeoRefSchema.extend({ isActive: z.boolean() }).nullable(),
  state: DestinationGeoRefSchema.nullable(),
  city: DestinationGeoRefSchema.nullable(),
  isActive: z.boolean(),
  /// Coarse "international vs domestic" flag (operator-set on the master).
  isInternational: z.boolean(),
  /// Travel-time tier - nullable until classified.
  haul: DestinationHaulSchema.nullable(),
  /// Optional cover image, stored as a media asset id. Null until set.
  coverImageMediaId: UuidSchema.nullable(),
  /// Ordered gallery of up to 3 landscape (horizontal) images for the
  /// destination PDF. Empty array until set; order is the PDF/display order.
  galleryLandscapeMediaIds: z.array(UuidSchema),
  /// The single portrait (vertical) PDF image - the 4th slot. Null until set.
  galleryPortraitMediaId: UuidSchema.nullable(),
  /// Dynamic trip-types this destination supports (FIT / Group / Cruise / ...).
  /// Comes back as a deduped sorted list - the join table is opaque to clients.
  tripTypes: z.array(TripTypeRefSchema),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type DestinationListItem = z.infer<typeof DestinationListItemSchema>;

export const DestinationListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  regionId: UuidSchema.optional(),
  countryId: UuidSchema.optional(),
  /// When true (the default for the lead-form picker), rows whose
  /// country is inactive or soft-deleted are excluded server-side.
  onlyActiveCountries: BooleanQueryParamSchema.optional(),
  /// User-form filters: narrow the destinations picker by dimension.
  /// All three are independent - omit any to skip that filter.
  /// Uses the string-aware boolean preprocessor so `?isInternational=false`
  /// actually filters to domestic rows - the plain `z.coerce.boolean()`
  /// has the well-known JS quirk where `Boolean("false") === true`.
  isInternational: BooleanQueryParamSchema.optional(),
  haul: DestinationHaulSchema.optional(),
  /// UUID of a TripType. Returns destinations that advertise this
  /// trip-type via the destination_trip_types join.
  tripTypeId: UuidSchema.optional(),
  /// When true, only destinations that have at least one live MICE package
  /// (packages.is_mice) are returned. Used by the MICE CMS section picker.
  hasMicePackages: BooleanQueryParamSchema.optional(),
});
export type DestinationListQuery = z.infer<typeof DestinationListQuerySchema>;

export const DestinationListResponseSchema = paginatedResponseSchema(DestinationListItemSchema);
export type DestinationListResponse = z.infer<typeof DestinationListResponseSchema>;

/**
 * Required: `name` + at least one of (`regionId`, `countryId`).
 * Optional: everything else. The "at least one" rule lets marketing
 * capture pure-region campaigns ("Southeast Asia Beach Holidays")
 * AND pure-country campaigns ("Japan") without forcing one to fake
 * the other. State + City further narrow when needed.
 */
export const CreateDestinationRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    regionId: UuidSchema.nullable().optional(),
    countryId: UuidSchema.nullable().optional(),
    stateId: UuidSchema.nullable().optional(),
    cityId: UuidSchema.nullable().optional(),
    isActive: z.boolean().optional(),
    isInternational: z.boolean().optional(),
    haul: DestinationHaulSchema.nullable().optional(),
    /// Trip-types the destination supports. Send `[]` to clear; omit
    /// to set the empty default at create time.
    tripTypeIds: z.array(UuidSchema).optional(),
    /// Optional cover image (media asset id). Null clears it; omit to leave unset.
    coverImageMediaId: UuidSchema.nullable().optional(),
    /// Up to 3 landscape PDF images, in display order. Omit for the empty default.
    galleryLandscapeMediaIds: z.array(UuidSchema).max(3).optional(),
    /// Single portrait PDF image (4th slot). Null clears; omit to leave unset.
    galleryPortraitMediaId: UuidSchema.nullable().optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
  })
  .refine((v) => Boolean(v.regionId) || Boolean(v.countryId), {
    message: 'Pick a Region or a Country (at least one is required).',
    path: ['regionId'],
  });
export type CreateDestinationRequest = z.infer<typeof CreateDestinationRequestSchema>;

export const UpdateDestinationRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    regionId: UuidSchema.nullable().optional(),
    countryId: UuidSchema.nullable().optional(),
    stateId: UuidSchema.nullable().optional(),
    cityId: UuidSchema.nullable().optional(),
    isActive: z.boolean().optional(),
    isInternational: z.boolean().optional(),
    haul: DestinationHaulSchema.nullable().optional(),
    /// PATCH semantic: undefined leaves the existing set unchanged;
    /// any other value (including `[]`) overwrites the join.
    tripTypeIds: z.array(UuidSchema).optional(),
    /// PATCH semantic: undefined leaves it unchanged; null clears the image.
    coverImageMediaId: UuidSchema.nullable().optional(),
    /// PATCH semantic: undefined leaves the gallery unchanged; any array
    /// (including `[]`) overwrites. Capped at 3 landscape images.
    galleryLandscapeMediaIds: z.array(UuidSchema).max(3).optional(),
    /// PATCH semantic: undefined leaves it unchanged; null clears the image.
    galleryPortraitMediaId: UuidSchema.nullable().optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
  })
  .refine(
    (v) => {
      // Partial patches that touch neither region nor country leave
      // the existing pair intact - the DB still satisfies the rule.
      if (v.regionId === undefined && v.countryId === undefined) return true;
      // Otherwise at least one of the supplied (or pre-existing,
      // when only one is touched) values must resolve to a uuid.
      // We can only check the payload here; the service does the
      // merged-state check for the half-supplied corner case.
      return Boolean(v.regionId) || Boolean(v.countryId);
    },
    {
      message: 'Pick a Region or a Country (at least one is required).',
      path: ['regionId'],
    },
  );
export type UpdateDestinationRequest = z.infer<typeof UpdateDestinationRequestSchema>;

// ----- Destination options (public dropdown feed) --------------------------

/**
 * Slim `{ id, name, isInternational }` row for a destination dropdown.
 * Mirrors the `/options` convention the other masters follow (see
 * QuerySourceOptionSchema). Kept deliberately narrow: this is the only
 * projection of the Destination master exposed on the PUBLIC website
 * endpoint, so it must not leak operator-only columns.
 */
export const DestinationOptionSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  isInternational: z.boolean(),
});
export type DestinationOption = z.infer<typeof DestinationOptionSchema>;

export const DestinationOptionListSchema = z.array(DestinationOptionSchema);
export type DestinationOptionList = z.infer<typeof DestinationOptionListSchema>;

export const DestinationOptionsQuerySchema = z.object({
  /** Case-insensitive contains-match on the destination name. */
  q: z.string().trim().min(1).max(120).optional(),
  isInternational: BooleanQueryParamSchema.optional(),
});
export type DestinationOptionsQuery = z.infer<typeof DestinationOptionsQuerySchema>;
